import { useInvitationEmailAttachments } from '@/hooks/use-invitation-email-attachments';
import { usePreparedInvitationAttachments } from '@/hooks/use-prepared-invitation-attachments';
import { EmailConnectPrompt } from '@/components/integrations/EmailConnectPrompt';
import { toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/auth-context';
import { useInvitationStoreSelector } from '@/contexts/invitation-context';
import { usePrepareTemplate } from '@/hooks/actions';
import { useTrackedInvitationOutreach } from '@/hooks/actions/use-tracked-invitation-outreach';
import { useEmailIntegrationStatus } from '@/hooks/use-email-integration-status';
import { InvitationMethod } from '@/types/invitation';
import { Button, VStack } from '@chakra-ui/react';
import { TemplateType } from '@fluenceur/types';
import type { AvailableVariable } from '@fluenceur/ui';
import { useCallback, useMemo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { OutreachEmailComposer, type OutreachEmailState } from './outreach/outreach-email-composer';
import { AiPersonalizeToggle } from './components';
import { useOutreachComposerAssembly } from './outreach/use-outreach-composer-assembly';
import { useOutreachLanguage } from './outreach/use-outreach-language';

interface ProposeCollabEmailStepProps {
  /** Modal close callback; `{ success: true }` after the email is queued. */
  onClose: ({ success }: { success?: boolean }) => void;
}

/**
 * EMAIL channel of the propose-collaboration flow for a single creator whose
 * email the brand already knows.
 *
 * A thin caller of the shared OutreachEmailComposer: it injects the body source
 * + the `{{invitation_link}}` requirement, and owns the send through the proven
 * outreach-email endpoint. The whole template wiring (library selector +
 * save-as-template, seed guard, body/key resolution, subject) comes from the
 * shared composer assembly; the prepared program-scoped
 * `CREATOR_OUTREACH_EMAIL_INVITATION` template is the fallback body. Compiled
 * HTML keeps variable chips as `{{key}}` mustache text for the backend job to
 * resolve. Deliberate omission vs. the bulk wishlist branch: no AI
 * personalization.
 */
const ProposeCollabEmailStep: FC<ProposeCollabEmailStepProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const files = useInvitationEmailAttachments();
  const { user } = useAuth();
  const invitationData = useInvitationStoreSelector((state) => state.invitationData);
  const selectedProgram = useInvitationStoreSelector((state) => state.selectedProgram);
  const aiPersonalize = useInvitationStoreSelector((state) => state.aiPersonalize);
  const setAiPersonalize = useInvitationStoreSelector((state) => state.setAiPersonalize);
  const { selectedLanguage, brandDefaultLanguage } = useOutreachLanguage();
  // Strict send capability: a mailbox needing a reconnect cannot send, so it
  // gates the compose exactly like the sponsored flow does.
  const { hasUsableMailbox, isLoading: isIntegrationLoading } = useEmailIntegrationStatus();

  const invitationId = invitationData?.invitationId ?? '';

  // Only push a language into the query when it differs from the brand default —
  // toggling ON without changing the locale should not trigger a redundant fetch.
  const overrideLanguage = selectedLanguage !== brandDefaultLanguage ? selectedLanguage : undefined;

  // Both template sources are gated on a usable mailbox: without one this step
  // renders the connect prompt, so fetching would throw two GETs away.
  const { data: emailTemplateData, isLoading: isLoadingTemplate } = usePrepareTemplate(
    selectedProgram?.id || '',
    TemplateType.CREATOR_OUTREACH_EMAIL_INVITATION,
    {
      enabled: !!selectedProgram?.id && hasUsableMailbox,
      language: overrideLanguage,
    }
  );

  usePreparedInvitationAttachments(emailTemplateData?.template?.attachments);

  const preparedContent = emailTemplateData?.template?.contentLexicalJson ?? null;

  const availableVariables: AvailableVariable[] = useMemo(() => {
    if (!emailTemplateData?.variables) return [];
    return emailTemplateData.variables.map((variable) => ({
      key: variable.key,
      label: variable.label,
      labelKey: variable.labelKey,
      description: variable.description,
      descriptionKey: variable.descriptionKey,
      required: variable.required,
      value: variable.value || '{{auto}}',
      disabled: variable.disabled,
      disabledReason: variable.disabledReason
        ? t(`invitations.editor.${variable.disabledReason}`)
        : undefined,
    }));
  }, [emailTemplateData, t]);

  // Shared composer assembly: template library flow (selector + save), seed
  // guard, body/key resolution and localized subject. No brandId → no library
  // fetch: same usable-mailbox gate as the prepared template above.
  const {
    composerKey,
    editorContent,
    editorFooter,
    saveAsTemplatePanel,
    subjectRow,
    subject,
    isSubjectValid,
    registerEmission,
    baseTemplateId,
  } = useOutreachComposerAssembly({
    attachmentControls: files,
    brandId: hasUsableMailbox ? user?.currentBrand?.id : undefined,
    method: InvitationMethod.EMAIL,
    fallbackContent: preparedContent,
    availableVariables,
    controlsDisabled: isLoadingTemplate,
    subjectKind: 'affiliation',
    italianSubject: emailTemplateData?.defaultSubjects?.it,
  });

  // Latest composer state: the compiled HTML is what we ship, `isValid` gates the
  // send button. The composer owns the editor, language toggle and variable guard.
  // The snapshot lives in the STORE (via the assembly's emission mirror).
  const [emailState, setEmailState] = useState({ bodyHtml: '', isValid: true });

  const handleComposerChange = useCallback(
    (state: OutreachEmailState) => {
      setEmailState({ bodyHtml: state.bodyHtml, isValid: state.isValid });
      registerEmission(state.messageSnapshot);
    },
    [registerEmission]
  );

  const { send: sendOutreachEmail, isSending } = useTrackedInvitationOutreach();

  const handleSend = useCallback(() => {
    if (!invitationId || !emailState.bodyHtml || !isSubjectValid || files.isBlocked) {
      toaster.create({
        title: t('invitationOutreach.send.error.generic'),
        type: 'error',
        duration: 3000,
      });
      return;
    }
    void sendOutreachEmail(
      {
        invitationId,
        subject,
        body: emailState.bodyHtml,
        attachmentIds: files.attachments.map((file) => file.id),
        baseTemplateId,
        aiPersonalizationEnabled: aiPersonalize,
      },
      {
        onComplete: () => onClose({ success: true }),
        onQueued: () => onClose({ success: true }),
      }
    );
  }, [
    invitationId,
    files.attachments,
    files.isBlocked,
    emailState.bodyHtml,
    isSubjectValid,
    subject,
    baseTemplateId,
    aiPersonalize,
    sendOutreachEmail,
    onClose,
    t,
  ]);

  // Email needs a USABLE mailbox (a needs-reconnect token cannot send). With
  // none, prompt to connect instead of showing a composer that can't send. No
  // copy-link field in this mode: the shareable link is the LINK channel's own
  // surface, and "Change" on the banner is one click away.
  if (!isIntegrationLoading && !hasUsableMailbox) {
    return (
      <VStack align="stretch" w="full" gap={4}>
        <EmailConnectPrompt
          title={t('invitations.modal.collab.connectMailbox.title')}
          description={t('invitations.modal.collab.connectMailbox.description')}
        />
      </VStack>
    );
  }

  return (
    <VStack align="stretch" w="full" gap={4}>
      {subjectRow}

      <OutreachEmailComposer
        key={composerKey}
        templateSource={{
          initialContent: editorContent,
          availableVariables,
          isLoading: isLoadingTemplate,
        }}
        requiredLinkVariable="{{invitation_link}}"
        onChange={handleComposerChange}
        editorFooter={editorFooter}
        attachmentControl={{
          attachments: files.attachments,
          onFileSelect: files.handleFileSelect,
          onRemoveAttachment: files.handleRemoveAttachment,
          isUploading: files.isUploading,
          deletingId: files.deletingId,
          issues: files.issues,
          onDismissIssue: files.dismissIssue,
          validationError: files.validationError,
          maxTotalBytes: files.maxTotalBytes,
        }}
      />

      <AiPersonalizeToggle
        checked={aiPersonalize}
        onCheckedChange={({ checked }) => setAiPersonalize(checked)}
        creatorCount={1}
        disabled={isSending}
      />

      <Button
        w="full"
        colorPalette="primary"
        onClick={handleSend}
        loading={isSending}
        disabled={
          isSending ||
          files.isBlocked ||
          !emailState.bodyHtml ||
          !emailState.isValid ||
          !isSubjectValid ||
          isLoadingTemplate
        }
      >
        {t('invitations.modal.invitationDetails.sendByEmail')}
      </Button>

      {saveAsTemplatePanel}
    </VStack>
  );
};

export default ProposeCollabEmailStep;
